import { formatDate } from "@/lib/format-data";

import { UnitConversions } from "@/types/enums/unit";
import { MeasurementSummary } from "@/types/measurement";

import { type ClinicClock, clinicClock } from "./clinic-clock";

export interface WeightData {
  date: string;
  Weight: number; // in lbs
  rawDate: string;
  rawWeight: number; // in kg
}

export interface WeightDifferences {
  totalWeightChange: number | null; // in lbs (matches weight chart naming)
  recentWeightChange: number | null; // in lbs (matches weight chart naming)
  firstDate: string | null;
  lastDate: string | null;
}

export interface WeightInsightsAtDate {
  changeSinceLastVisit: number | null; // Change from previous measurement (lbs)
  totalWeightChange: number | null; // Change from first measurement (lbs)
  previousMeasurementDate: string | null;
  firstMeasurementDate: string | null;
}

/**
 * Process weight measurements - group by date and use latest measurement for each day
 * This matches the logic used in weight-chart.tsx
 */
export function processWeightData(
  measurements?: MeasurementSummary[],
): WeightData[] {
  const measWithWeight = (measurements ?? []).filter(
    (m: MeasurementSummary) => m.weight != null && !isNaN(m.weight),
  );

  const dateMap = new Map<string, MeasurementSummary>();

  measWithWeight.forEach((measurement) => {
    const dateKey = clinicClock().dayOf(measurement.measurementDate);

    if (
      !dateMap.has(dateKey) ||
      new Date(measurement.measurementDate).getTime() >
        new Date(dateMap.get(dateKey)!.measurementDate).getTime()
    ) {
      dateMap.set(dateKey, measurement);
    }
  });

  const sortedMeasurements = Array.from(dateMap.values()).sort(
    (a, b) =>
      new Date(a.measurementDate).getTime() -
      new Date(b.measurementDate).getTime(),
  );

  return sortedMeasurements
    .map((m: MeasurementSummary) => {
      const weightLbs = UnitConversions.kgToLbs(m.weight);
      if (weightLbs == null || isNaN(weightLbs)) return null;

      return {
        date: formatDate(m.measurementDate, true, false),
        Weight: Number(weightLbs.toFixed(1)),
        rawDate: m.measurementDate,
        rawWeight: m.weight!,
      };
    })
    .filter((item): item is WeightData => item !== null);
}

/**
 * Calculate weight differences using the same logic as weight-chart.tsx
 */
export function calculateWeightDifferences(
  measurements?: MeasurementSummary[],
): WeightDifferences | null {
  const weightData = processWeightData(measurements);

  if (weightData.length < 2) {
    return null;
  }

  // Current weight (latest measurement)
  const currentMeasurement = weightData[weightData.length - 1];
  const currentWeight = Number(currentMeasurement.Weight.toFixed(1));

  // Initial weight (first measurement)
  const initialMeasurement = weightData[0];
  const initialWeight = Number(initialMeasurement.Weight.toFixed(1));

  // Previous weight (second to last measurement)
  const previousMeasurement = weightData[weightData.length - 2];
  const previousWeight = Number(previousMeasurement.Weight.toFixed(1));

  // Calculate changes in lbs (exactly matching weight-chart.tsx)
  const totalWeightChange = Number((currentWeight - initialWeight).toFixed(1));
  const recentWeightChange = Number(
    (currentWeight - previousWeight).toFixed(1),
  );

  return {
    totalWeightChange,
    recentWeightChange,
    firstDate: initialMeasurement.rawDate,
    lastDate: previousMeasurement.rawDate,
  };
}

/**
 * Get the latest weight measurement (processed weight data)
 */
export function getLatestWeight(
  measurements?: MeasurementSummary[],
): WeightData | null {
  const weightData = processWeightData(measurements);
  return weightData.length > 0 ? weightData[weightData.length - 1] : null;
}

/**
 * Get the latest weight in kg (raw measurement)
 */
export function getLatestWeightKg(
  measurements?: MeasurementSummary[],
): number | null {
  const latestWeight = getLatestWeight(measurements);
  return latestWeight ? latestWeight.rawWeight : null;
}

/**
 * Get the latest weight in lbs (processed weight, matches weight chart)
 */
export function getLatestWeightLbs(
  measurements?: MeasurementSummary[],
): number | null {
  const latestWeight = getLatestWeight(measurements);
  return latestWeight ? latestWeight.Weight : null;
}

/**
 * Calculate weight insights relative to a specific reference date.
 * This is used in notes to show weight changes relative to when the note was created,
 * so historical notes show accurate historical context.
 *
 * @param measurements - Array of patient measurements
 * @param currentWeightLbs - The current weight value in pounds (from the note answer)
 * @param referenceDate - The date to use as reference (typically note creation date)
 * @param clock - The clock days are compared on; components pass `useClinicClock()`
 * @returns Weight change insights with dates, or nulls if not enough data
 */
export function calculateWeightInsightsAtDate(
  measurements: MeasurementSummary[] | undefined,
  currentWeightLbs: number,
  referenceDate: Date | string,
  clock: ClinicClock,
): WeightInsightsAtDate {
  const nullResult: WeightInsightsAtDate = {
    changeSinceLastVisit: null,
    totalWeightChange: null,
    previousMeasurementDate: null,
    firstMeasurementDate: null,
  };

  if (!measurements || measurements.length === 0) {
    return nullResult;
  }

  // Convert reference date to Date object if it's a string
  const refDate =
    typeof referenceDate === "string" ? new Date(referenceDate) : referenceDate;

  // Filter to only measurements with valid weight that are BEFORE the reference date (by day).
  // Using day-level comparison ensures same-day measurements (e.g. check-in vitals taken
  // earlier in the day) are excluded, so "change since last visit" compares against a
  // previous visit rather than the current visit's check-in measurement.
  const refDateKey = clock.dayOf(refDate);
  const previousMeasurements = measurements
    .filter((m) => {
      if (m.weight == null || isNaN(m.weight)) return false;
      return clock.dayOf(m.measurementDate) < refDateKey;
    })
    .sort(
      (a, b) =>
        new Date(a.measurementDate).getTime() -
        new Date(b.measurementDate).getTime(),
    );

  if (previousMeasurements.length === 0) {
    return nullResult;
  }

  // First measurement (earliest)
  const firstMeasurement = previousMeasurements[0];
  const firstWeightLbs = UnitConversions.kgToLbs(firstMeasurement.weight!);

  // Previous measurement (most recent before reference date)
  const lastPreviousMeasurement =
    previousMeasurements[previousMeasurements.length - 1];
  const previousWeightLbs = UnitConversions.kgToLbs(
    lastPreviousMeasurement.weight!,
  );

  // If conversions failed, return null result
  if (previousWeightLbs === null || firstWeightLbs === null) {
    return nullResult;
  }

  // Calculate changes
  const changeSinceLastVisit = Number(
    (currentWeightLbs - previousWeightLbs).toFixed(1),
  );
  const totalWeightChange = Number(
    (currentWeightLbs - firstWeightLbs).toFixed(1),
  );

  return {
    changeSinceLastVisit,
    totalWeightChange,
    previousMeasurementDate: lastPreviousMeasurement.measurementDate,
    firstMeasurementDate: firstMeasurement.measurementDate,
  };
}
